import axios, { type InternalAxiosRequestConfig } from 'axios';
import { BRAND_OFFERS_ADMIN_AUTH_CHANGED_EVENT } from '@/contexts/BrandOffersAdminAuthContext/BrandOffersAdminAuthContext.constants';
import {
  clearBrandOffersAdminSession,
  readBrandOffersAdminToken,
} from '@/contexts/BrandOffersAdminAuthContext/brandOffersAdminSession';

const BRAND_OFFER_API_ROUTES = ['/api/offers'] as const;
const CURATOR_POST_API_ROUTES = ['/homepage/curator/posts'] as const;
const SIMILAR_PRODUCTS_API_ROUTES = [
  '/api/similar-products',
  '/api/text-search',
] as const;

function isBrandOfferApiUrl(url: string | undefined): boolean {
  if (!url) return false;
  return BRAND_OFFER_API_ROUTES.some(
    (route) => url === route || url.startsWith(`${route}/`)
  );
}

function isCuratorPostApiUrl(url: string | undefined): boolean {
  if (!url) return false;
  return CURATOR_POST_API_ROUTES.some(
    (route) => url === route || url.startsWith(`${route}/`)
  );
}

function isSimilarProductsApiUrl(url: string | undefined): boolean {
  if (!url) return false;
  return SIMILAR_PRODUCTS_API_ROUTES.some(
    (route) => url === route || url.startsWith(`${route}/`)
  );
}

const slaydApi = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.slayd.in',
});

slaydApi.interceptors.request.use(
  (config) => {
    let token: string | null = null;

    if (isBrandOfferApiUrl(config.url)) {
      token = readBrandOffersAdminToken();
    } else if (isCuratorPostApiUrl(config.url)) {
      token = import.meta.env.VITE_CURATOR_ADMIN_TOKEN || null;
    } else if (isSimilarProductsApiUrl(config.url)) {
      token = import.meta.env.VITE_SIMILAR_PRODUCTS_API_TOKEN || null;
    } else {
      token = localStorage.getItem('token');
    }

    if (token) {
      config.headers.Authorization = `Token ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

slaydApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const config = error.config as InternalAxiosRequestConfig | undefined;

      if (isBrandOfferApiUrl(config?.url)) {
        clearBrandOffersAdminSession();
        window.dispatchEvent(new Event(BRAND_OFFERS_ADMIN_AUTH_CHANGED_EVENT));
        if (!window.location.pathname.startsWith('/login')) {
          window.location.assign('/login');
        }
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('userName');
      }
    }

    return Promise.reject(error);
  }
);

export default slaydApi;
